import {
  boolean,
  InferType,
  object,
  string,
  TestContext,
  ValidationError,
} from 'yup'
import { differenceInYears } from 'date-fns'
import { isAlphabet, isNumber, parseDate } from '@/lib/utils'
import { AU_MOBILE_REGEX, EMAIL_REGEX } from '@/lib/constants'
import { WebConfig } from '@/lib/config'

const BaseProfileValidator = object({
  firstName: string()
    .default('')
    .max(40, 'First name must be less than 40 characters in length')
    .required('First name is required'),
  lastName: string()
    .default('')
    .max(40, 'Last name must be less than 40 characters in length')
    .required('Last name is required'),
  email: string()
    .default('')
    .required('Email is required')
    .matches(EMAIL_REGEX, 'Invalid email address'),
  mobile: string()
    .default('')
    .required('Mobile number is required')
    // The backend rejects anything that isn't an Australian mobile before it
    // reaches Twilio, so a 10-digit landline must not get past the form.
    .matches(AU_MOBILE_REGEX, {
      excludeEmptyString: true,
      message: 'Enter an Australian mobile number starting with 04',
    }),
  formattedAddress: string().default('').required('Address is required'),
  suburb: string().default('').required('Suburb is required'),
  state: string().default('').required('State is required'),
  postcode: string()
    .default('')
    .required('Postcode is required')
    .min(4, 'Postcode must have 4 numbers')
    .max(4, 'Postcode must have 4 numbers')
    .matches(/[0-9]*/, 'Postcode must have 4 numbers'),
  dob: string()
    .default('')
    .transform((value) => {
      if (!value) return ''
      return value
    })
    .required('Date of birth is required')
    .test('must-be-at-least-18-years', (value, ctx) => {
      const date = parseDate(value)
      if (differenceInYears(new Date(), date) < 18) {
        return ctx.createError({
          message: 'You must be at least 18 years old to use our platform',
        })
      }
      if (differenceInYears(new Date(), date) > 100) {
        return ctx.createError({
          message: 'You cannot be older than 100 years!',
        })
      }
      return true
    }),
  gender: string()
    .default('')
    .transform((value) => {
      if (!value) return ''
      return value
    })
    .required('You need to select your gender')
    .oneOf(['M', 'F'], 'You need to select your gender'),
})

export const PatientProfileValidator = BaseProfileValidator.shape({
  concessionNumber: string()
    .default('')
    .test({
      name: 'is-valid-concession-number',
      skipAbsent: true,
      test(value, ctx) {
        // Saved identifiers cannot block forms where these fields are hidden.
        if (!WebConfig.prescriptionConformanceMode) return true
        if (value.length > 0) {
          const error = ctx.createError({
            message:
              'Invalid concession card number, it should have 9 digits followed by a character',
          })

          if (value.length != 10) return error
          for (let idx = 0; idx < 9; idx++) {
            if (Number.isNaN(Number.parseInt(value.charAt(idx)))) return error
          }
          if (!isAlphabet(value.charAt(9))) return error
        }

        return true
      },
    }),

  entitlementNumber: string()
    .default('')
    .test({
      name: 'is-valid-entitlement-number',
      skipAbsent: true,
      test(value, ctx) {
        if (!WebConfig.prescriptionConformanceMode) return true
        if (value.length > 0) {
          const error = ctx.createError({
            message:
              'Invalid entitlement number, it should starts with SN followed by 9 digits',
          })

          if (value.length !== 11 || !value.startsWith('SN')) return error
          for (let idx = 2; idx < value.length; idx++) {
            if (!isNumber(value.charAt(idx))) return error
          }
        }

        return true
      },
    }),

  isAbOrTorIslander: boolean().default(false),
})
export type PatientProfileForm = InferType<typeof PatientProfileValidator>

export const DoctorProfileValidator = BaseProfileValidator.shape({
  aphraRegNo: string()
    .default('')
    .transform((value) => {
      if (!value) return ''
      return value
    })
    .required('APHRA Registration Number is required'),
  signatureUrl: string()
    .default('')
    .transform((value) => {
      if (!value) return ''
      return value
    })
    .required('Signature is required'),
  prescriberNumber: string().default('').nullable(),
  moRoutingId: string().default('').nullable(),
  gstRegistered: boolean().default(false),
})

export function optionalEmailTester(message: string) {
  return (
    value: string | undefined,
    context: TestContext<any>,
  ): void | boolean | ValidationError | Promise<boolean | ValidationError> => {
    if (value && value.length > 0 && !EMAIL_REGEX.test(value))
      return context.createError({ message })
    return true
  }
}

export function optionalPhoneTester(message: string) {
  return (
    value: string | undefined,
    context: TestContext<any>,
  ): void | boolean | ValidationError | Promise<boolean | ValidationError> => {
    if (value && value.length > 0 && !/^\d{10}$/.test(value))
      return context.createError({ message })
    return true
  }
}
